import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui";
import { useCopy } from "../i18n/locale";

/**
 * Public SeaGM marketing homepage.
 *
 * `/` must stay public for unauthenticated visitors. Do not wrap this route (or
 * reintroduce a pathless `/_authed` layout that redirects to `/login`). An empty
 * `/_authed` route previously competed with this Index route for `/` and sent
 * guests to Study Registration. Protect only authenticated pages (e.g. `/profile`)
 * with their own per-route checks. Keep `/get-started`, `/recruitment`,
 * `/waitlist`, `/login`, and `/logged-out` public as well.
 */
export const Route = createFileRoute("/")({
    component: Home,
});

function Home() {
    const copy = useCopy().home;
    return (
        <div className="min-h-screen">
            <div className="mx-auto px-4 pt-6 pb-12 max-w-6xl">
                {/* Hero Section */}
                <div className="mb-16 text-center">
                    <div className="flex justify-center mb-6">
                        <svg
                            width="240"
                            height="240"
                            viewBox="0 0 240 240"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M60 160.82H99.4521L112.151 139L131 202.5L150.476 160.82H180"
                                stroke="#1e293b"
                                stroke-width="10"
                            />
                            <path
                                d="M116.5 104L91.5 146.75H78L66.5 99L123.5 77.5L179.5 99L167 146.75H141L134 162.5L116.5 104Z"
                                fill="#1e293b"
                                stroke="#1e293b"
                                stroke-width="10"
                            />
                            <path
                                d="M82.5 94V63C82.5 61.8954 83.3954 61 84.5 61H160.5C161.605 61 162.5 61.8954 162.5 63V96.5"
                                stroke="#1e293b"
                                stroke-width="10"
                            />
                            <rect x="105.5" y="38.5" width="35" height="25" fill="#1e293b" stroke="#1e293b" />
                        </svg>
                    </div>
                    <h1 className="mb-6 font-bold text-slate-800 text-5xl">{copy.title}</h1>
                    <p className="mx-auto mb-8 max-w-3xl text-slate-600 text-2xl">{copy.subtitle}</p>
                    <div className="flex sm:flex-row flex-col justify-center gap-4">
                        <Link to="/get-started">
                            <Button
                                variant="default"
                                className="bg-indigo-600 hover:bg-indigo-700 px-8 py-4 rounded-lg font-semibold text-white text-lg transition-colors"
                            >
                                {copy.join}
                            </Button>
                        </Link>
                        <Link to="/recruitment">
                            <Button variant="secondary">{copy.learnMore}</Button>
                        </Link>
                    </div>
                    <p className="mx-auto mt-4 max-w-2xl text-slate-500 text-base">{copy.publicNote}</p>
                </div>

                {/* Benefits Section */}
                <div className="gap-8 grid md:grid-cols-2 mb-16">
                    <div className="bg-white shadow-sm p-8 border rounded-xl">
                        <h2 className="mb-4 font-bold text-slate-900 text-2xl">{copy.whatYouGet}</h2>
                        <ul className="space-y-3 text-slate-600 text-lg">
                            {copy.benefits.map((benefit) => (
                                <li key={benefit} className="flex items-start">
                                    <span className="mr-3 text-green-500">✓</span>
                                    {benefit}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-white shadow-sm p-8 border rounded-xl">
                        <h2 className="mb-4 font-bold text-slate-900 text-2xl">{copy.inclusion}</h2>
                        <ul className="space-y-3 text-slate-600 text-lg">
                            {copy.criteria.map((criterion) => (
                                <li key={criterion} className="flex items-start">
                                    <span className="mr-3 text-blue-500">•</span>
                                    {criterion}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* How It Works */}
                <div className="mb-16 text-center">
                    <h2 className="mb-8 font-bold text-slate-900 text-3xl">{copy.howItWorks}</h2>
                    <div className="gap-8 grid md:grid-cols-3">
                        <div className="text-center">
                            <div className="flex justify-center items-center bg-indigo-100 mx-auto mb-4 rounded-full w-16 h-16">
                                <span className="text-2xl">📱</span>
                            </div>
                            <h3 className="mb-2 font-semibold text-xl">{copy.wearTitle}</h3>
                            <p className="text-slate-600 text-lg">{copy.wearBody}</p>
                        </div>
                        <div className="text-center">
                            <div className="flex justify-center items-center bg-indigo-100 mx-auto mb-4 rounded-full w-16 h-16">
                                <span className="text-2xl">💬</span>
                            </div>
                            <h3 className="mb-2 font-semibold text-xl">{copy.checkInTitle}</h3>
                            <p className="text-slate-600 text-lg">{copy.checkInBody}</p>
                        </div>
                        <div className="text-center">
                            <div className="flex justify-center items-center bg-indigo-100 mx-auto mb-4 rounded-full w-16 h-16">
                                <span className="text-2xl">🏥</span>
                            </div>
                            <h3 className="mb-2 font-semibold text-xl">{copy.meetingsTitle}</h3>
                            <p className="text-slate-600 text-lg">{copy.meetingsBody}</p>
                        </div>
                    </div>
                </div>

                {/* The Problem */}
                <div className="mb-16 rounded-xl">
                    <h2 className="mb-6 font-bold text-slate-900 text-3xl text-center">{copy.whyTitle}</h2>
                    <div className="mx-auto max-w-4xl">
                        <p className="mb-6 text-slate-600 text-xl text-center">{copy.whyIntro}</p>
                        <div className="gap-6 grid md:grid-cols-2">
                            <div className="bg-white p-6 rounded-lg">
                                <h3 className="mb-3 font-semibold text-red-600">{copy.restrictionsTitle}</h3>
                                <ul className="space-y-2 text-slate-600 text-lg">
                                    {copy.restrictions.map((restriction) => (
                                        <li key={restriction}>• {restriction}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white p-6 rounded-lg">
                                <h3 className="mb-3 font-semibold text-green-600">{copy.solutionTitle}</h3>
                                <p className="text-slate-600 text-lg">{copy.solutionBody}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-indigo-600 p-8 rounded-xl text-white text-center">
                    <h2 className="mb-4 font-bold text-3xl">{copy.ctaTitle}</h2>
                    <p className="opacity-90 mb-8 text-2xl">{copy.ctaBody}</p>
                    <Link to="/get-started">
                        <button className="bg-white hover:bg-slate-100 px-8 py-4 rounded-lg font-semibold text-indigo-600 text-lg transition-colors">
                            {copy.join}
                        </button>
                    </Link>
                </div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-slate-200 border-t text-center">
                    <p className="text-slate-500 text-lg">{copy.footer}</p>
                </div>
            </div>
        </div>
    );
}
